/**
 * Pure helper for the focus-pane task status cycle.
 * Extracted from FocusView so it can be deterministically unit tested.
 *
 * Behavior (CEO spec clarified 2026-04-08 PM):
 *
 * The complete-circle is a "mark complete" toggle, NOT a forward cycle.
 * Clicking it from any non-done state moves the task directly to 'done'
 * and remembers where it came from. Clicking it from 'done' un-marks the
 * task back to the remembered state (fallback 'todo').
 *
 *   - 'todo'        -> 'done' (remember 'todo')
 *   - 'in_progress' -> 'done' (remember 'in_progress')
 *   - 'blocked'     -> 'done' (remember 'blocked')
 *   - 'done'        -> remembered previous, fallback 'todo' (map entry consumed)
 *
 * History:
 *   - 2026-04-06: original spec was a forward cycle (todo -> in_progress ->
 *     done -> previous). Shipped but was wrong per CEO intent.
 *   - 2026-04-08 AM: shape migration broke the cycle because it compared
 *     against display labels ('Done', 'In Progress', ...) while callers now
 *     pass slugs. Fixed the comparison form but kept the forward-cycle spec.
 *   - 2026-04-08 PM: CEO clarified the real spec — it's a toggle, not a
 *     cycle. Every non-done click goes straight to done. This file reflects
 *     that. tests/lib/taskStatus.test.js locks it in.
 *
 * The previousMap is mutated in place. Pass a Map keyed by task id.
 */
export function nextTaskStatus(currentStatus, previousMap, taskId) {
  if (currentStatus === 'done') {
    const restored = previousMap.get(taskId) || 'todo';
    previousMap.delete(taskId);
    return restored;
  }
  // Any non-done state: remember it, then jump directly to done.
  previousMap.set(taskId, currentStatus);
  return 'done';
}
